import {
  AlertTriangle,
  Ban,
  Building2,
  CheckCircle2,
  ClipboardList,
  CreditCard,
  FileDown,
  FilePenLine,
  Baby,
  FileText,
  Filter,
  Headphones,
  LayoutDashboard,
  ListChecks,
  MessageSquareWarning,
  Search,
  ShieldCheck,
  Siren,
  Table2,
  TrendingUp,
  UploadCloud,
  UserMinus,
  UserPlus,
  Users,
  type LucideIcon,
} from 'lucide-react';
import type { ViewKey } from '@lib/permissions/catalog';

/**
 * Las secciones del menú, en orden. Vive fuera de `Sidebar.tsx` para poder
 * importarse desde los tests sin arrastrar el componente (y porque exportar una
 * constante desde un archivo de componentes rompe el fast-refresh).
 *
 * `Sidebar.catalogo.spec.tsx` compara esta lista contra PERMISSION_CATALOG, que
 * es lo que pinta el editor de permisos de Usuarios: si se desincronizan
 * aparecen secciones que nadie puede gobernar — pasó con Rechazados y Alertas
 * Bajas, que viajaban con el permiso de otra sección.
 */

export interface Item {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Permiso de vista que habilita este item. El menú se arma con los items
   *  cuyo permiso esté en el set efectivo del usuario. */
  permiso: ViewKey;
}

// El orden es el del menú. La visibilidad la decide el permiso efectivo del
// usuario (defaults del rol ⊕ overrides), no el rol directamente.
//
// Se exporta para que `Sidebar.catalogo.spec.tsx` lo compare contra
// PERMISSION_CATALOG: cada sección del menú tiene que existir en el catálogo,
// que es lo que pinta el editor de permisos de Usuarios. Cuando se desincronizan
// aparecen secciones que nadie puede gobernar — pasó con Rechazados y Alertas
// Bajas, que durante meses viajaron con el permiso de otra sección.
export const ITEMS: Item[] = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, permiso: 'view:dashboard' },
  { to: '/busqueda', label: 'Búsqueda', icon: Search, permiso: 'view:busqueda' },
  { to: '/tmk-crm', label: 'CRM TMK', icon: Headphones, permiso: 'view:tmk-crm' },
  { to: '/skytel-funnel', label: 'Funnel SkyTel', icon: Filter, permiso: 'view:skytel-funnel' },
  { to: '/empresas', label: 'Organizaciones', icon: Building2, permiso: 'view:empresas' },
  { to: '/usuarios', label: 'Usuarios', icon: Users, permiso: 'view:usuarios' },
  { to: '/padrones', label: 'Padrones', icon: Table2, permiso: 'view:padrones' },
  { to: '/legajos-listos', label: 'Alta Grupos', icon: CheckCircle2, permiso: 'view:legajos-listos' },
  { to: '/legajos-bis', label: 'Alta Integrantes', icon: UserPlus, permiso: 'view:adherentes' },
  { to: '/rechazados', label: 'Rechazados', icon: AlertTriangle, permiso: 'view:rechazados' },
  {
    to: '/cuestionarios-salud',
    label: 'Cuestionarios de salud',
    icon: ClipboardList,
    permiso: 'view:cuestionarios-salud',
  },
  { to: '/observaciones', label: 'Observaciones', icon: MessageSquareWarning, permiso: 'view:observaciones' },
  { to: '/bloqueados', label: 'Bloqueados', icon: Ban, permiso: 'view:bloqueados' },
  { to: '/bajas', label: 'Bajas', icon: UserMinus, permiso: 'view:bajas' },
  // Un solo item: los dos flujos (Sensibles / No Sensibles) son tabs dentro de
  // la pantalla, porque este menú es plano y no soporta padre/hijo.
  {
    to: '/modificaciones',
    label: 'Modificación de datos',
    icon: FilePenLine,
    permiso: 'view:modificaciones',
  },
  { to: '/alertas-bajas', label: 'Alertas Bajas', icon: Siren, permiso: 'view:alertas-bajas' },
  { to: '/reporte-abm-web', label: 'Reporte ABM Web', icon: FileDown, permiso: 'view:reporte-abm-web' },
  { to: '/reporte-padron', label: 'Reporte de padrón', icon: ListChecks, permiso: 'view:reporte-padron' },
  { to: '/pagos-ms2', label: 'Pagos MS2', icon: CreditCard, permiso: 'view:pagos-ms2' },
  { to: '/alta-manual', label: 'Alta manual', icon: UploadCloud, permiso: 'view:alta-manual' },
  { to: '/reglas', label: 'Reglas', icon: ShieldCheck, permiso: 'view:reglas' },
  { to: '/documentacion', label: 'Documentación', icon: FileText, permiso: 'view:documentacion' },
  { to: '/ventas-comisiones', label: 'Ventas / Comisiones', icon: TrendingUp, permiso: 'view:ventas-comisiones' },
  // Recién nacidos (VEN-513): Binary los tiene con datos básicos, la organización los completa.
  { to: '/recien-nacidos', label: 'Recién nacidos', icon: Baby, permiso: 'view:recien-nacidos' },
];

